import 'sweetalert2/dist/sweetalert2.min.css';
import Swal from 'sweetalert2';

export type ToastVariant = 'success' | 'warning' | 'danger' | 'info';

export interface ToastEntry {
  id: number;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

const ACCENT_BY_VARIANT: Record<ToastVariant, string> = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  info: 'var(--info)',
};

const SVG_BY_VARIANT: Record<ToastVariant, string> = {
  success:
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15.5 6.5 8.5 13.5 5 10" /></svg>',
  warning:
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 5.5v5.25" /><path d="M10 14.1h.01" /></svg>',
  danger:
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m10 3.75 6.4 11.25H3.6L10 3.75Z" /><path d="M10 8v3.4" /><path d="M10 13.6h.01" /></svg>',
  info:
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="10" cy="10" r="7" /><path d="M10 9.25V13" /><path d="M10 6.75h.01" /></svg>',
};

const swalToast = Swal.mixin({
  toast: true,
  position: 'top',
  showConfirmButton: false,
  showCloseButton: true,
  timer: 3500,
  timerProgressBar: false,
  allowEnterKey: false,
  buttonsStyling: false,
  customClass: {
    container: 'ip4x-swal-toast-container',
    popup: 'ip4x-swal-toast',
    htmlContainer: 'ip4x-swal-toast-content',
    closeButton: 'ip4x-swal-toast-close',
  },
});

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderToastMarkup(variant: ToastVariant, title: string, description?: string): string {
  const safeTitle = escapeHtml(title);
  const safeDescription = description
    ? `<p class="ip4x-toast-shell__description">${escapeHtml(description).replace(/\n/g, '<br />')}</p>`
    : '';

  return `
    <div class="ip4x-toast-shell ip4x-toast-shell--${variant}${description ? '' : ' ip4x-toast-shell--compact'}">
      <span class="ip4x-toast-shell__icon" aria-hidden="true">${SVG_BY_VARIANT[variant]}</span>
      <div class="ip4x-toast-shell__body">
        <p class="ip4x-toast-shell__title">${safeTitle}</p>
        ${safeDescription}
      </div>
    </div>
  `;
}

function showToast(
  variant: ToastVariant,
  title: string,
  description?: string,
  duration?: number,
): number {
  const id = Date.now() + Math.random();
  void swalToast.fire({
    html: renderToastMarkup(variant, title, description),
    timer: duration ?? 3500,
    didOpen: (popup) => {
      popup.style.setProperty('--ip4x-toast-accent', ACCENT_BY_VARIANT[variant]);
      popup.addEventListener('mouseenter', Swal.stopTimer);
      popup.addEventListener('mouseleave', Swal.resumeTimer);
    },
  });
  return id;
}

export const toast = {
  success: (title: string, description?: string, duration?: number) =>
    showToast('success', title, description, duration),
  warning: (title: string, description?: string, duration?: number) =>
    showToast('warning', title, description, duration),
  danger: (title: string, description?: string, duration?: number) =>
    showToast('danger', title, description, duration),
  info: (title: string, description?: string, duration?: number) =>
    showToast('info', title, description, duration),
  dismiss: () => Swal.close(),
};

export function ToastViewport(): JSX.Element {
  return <></>;
}
